import { useLayoutEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  DeskLinkError,
  fetchAnswer,
  fetchDeskApprove,
  fetchDeskConfirm,
  fetchDeskConsent,
  resendDeskApproveCode,
  sendAnswer,
  submitDeskApprove,
  submitDeskConfirm,
  submitDeskConsent,
  TicketLinkGone,
  type DeskApproverProof,
} from '../api/easeedeskDesk'
import { CustomerTicketPage } from '../easeedesk/CustomerTicketPage'
import { Spinner } from '../ui/Spinner'
import './deskScreens.css'

const PAGE: React.CSSProperties = {
  margin: 0,
  padding: '1px 16px',
  font: '16px/1.55 system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
  background: '#f4f6f8',
  color: '#22303f',
  minHeight: '100vh',
}

/* `em`, nu `rem`: foile portalului pun `html { font-size: 10px }`, deci 34rem ar fi 340px. */
const CARD: React.CSSProperties = {
  maxWidth: '34em',
  margin: '12vh auto',
  padding: '1.5em',
  background: '#fff',
  borderRadius: 10,
  boxShadow: '0 1px 3px rgba(0,0,0,.12)',
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="od-desk" style={PAGE}>
      <div style={CARD}>{children}</div>
    </div>
  )
}

/**
 * Adresa paginii poarta jetonul personal din email: niciun Referer nu trebuie sa
 * il duca mai departe (o imagine, un link din comentariu, o cerere la API).
 * Politica de dinainte se pune la loc cand pagina pleaca.
 */
function useNoReferrer() {
  useLayoutEffect(() => {
    let meta = document.head.querySelector<HTMLMetaElement>('meta[name="referrer"]')
    const previous = meta?.content ?? null
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'referrer'
      document.head.appendChild(meta)
    }
    meta.content = 'no-referrer'
    const own = meta
    return () => {
      if (previous === null) own.remove()
      else own.content = previous
    }
  }, [])
}

type Lang = 'en' | 'ro'

/** Limba paginii: a tichetului cand a venit, altfel a browserului (un link expirat nu mai spune nimic). */
function pageLang(lang?: string): Lang {
  if (lang === 'ro' || lang === 'en') return lang
  const nav = typeof navigator !== 'undefined' ? navigator.language || '' : ''
  return nav.toLowerCase().startsWith('ro') ? 'ro' : 'en'
}

const TEXT = {
  en: {
    goneTitle: 'This link is no longer valid',
    goneBody: 'It was already used or it expired. Please reply to the ticket email, or ask your service desk to send the link again.',
    error: 'Something went wrong. Please try again.',
    stateChanged: 'This request was already handled or has changed since the email was sent. There is nothing more to do here.',
    selfApproval: 'You asked for this action, so you cannot approve it yourself. Another approver has to decide.',
    cabPending: 'The CAB has not voted on this change yet. Approve after the CAB vote.',
    cabRejected: 'The CAB rejected this change; it cannot be approved.',
    consentDeclined: 'The requester declined the proposed window; this change cannot be approved.',
    signIn: 'Sign in to the portal in this browser with your own account, then reload this page. A forwarded link does not work.',
    signInLink: 'Sign in',
    notTheApprover: 'You are signed in with another account. Only the approver this email was sent to can decide.',
    codeHint: 'We emailed an approval code to {to}, in a separate message. Enter it here.',
    codeNotSent: 'We could not email an approval code. Ask the service desk to send the link again.',
    codeLabel: 'Approval code',
    codeResend: 'Send a new code',
    codeResent: 'A new code is on its way.',
    codeRequired: 'Enter the approval code from your email.',
    codeWrong: 'The approval code is not right. Check the email and try again.',
    codeLocked: 'Too many wrong codes. Send a new code and try again.',
    codeExpired: 'The approval code expired. Send a new code.',
    codeLimit: 'Too many codes were sent today. Ask the service desk for help.',
  },
  ro: {
    goneTitle: 'Acest link nu mai este valabil',
    goneBody: 'A fost deja folosit sau a expirat. Raspunde la emailul tichetului sau cere biroului de service sa trimita linkul din nou.',
    error: 'Ceva nu a mers. Te rugam sa incerci din nou.',
    stateChanged: 'Aceasta cerere a fost deja rezolvata sau s-a schimbat de cand a plecat emailul. Nu mai e nimic de facut aici.',
    selfApproval: 'Tu ai cerut aceasta actiune, deci nu o poti aproba tu. Trebuie sa decida alt aprobator.',
    cabPending: 'CAB-ul nu a votat inca aceasta schimbare. Aproba dupa votul CAB.',
    cabRejected: 'CAB-ul a respins aceasta schimbare; nu mai poate fi aprobata.',
    consentDeclined: 'Solicitantul a refuzat fereastra propusa; schimbarea nu mai poate fi aprobata.',
    signIn: 'Autentifica-te in portal in acest browser, cu contul tau, apoi reincarca pagina. Linkul trimis altcuiva nu merge.',
    signInLink: 'Autentificare',
    notTheApprover: 'Esti autentificat cu alt cont. Decide numai aprobatorul caruia i-a fost trimis emailul.',
    codeHint: 'Ti-am trimis un cod de aprobare pe {to}, intr-un email separat. Scrie-l aici.',
    codeNotSent: 'Nu am putut trimite codul de aprobare. Cere biroului de service sa trimita linkul din nou.',
    codeLabel: 'Cod de aprobare',
    codeResend: 'Trimite un cod nou',
    codeResent: 'Un cod nou e pe drum.',
    codeRequired: 'Scrie codul de aprobare din email.',
    codeWrong: 'Codul de aprobare nu e corect. Verifica emailul si incearca din nou.',
    codeLocked: 'Prea multe coduri gresite. Cere un cod nou si incearca din nou.',
    codeExpired: 'Codul de aprobare a expirat. Cere un cod nou.',
    codeLimit: 'Au plecat prea multe coduri azi. Cere ajutorul biroului de service.',
  },
} as const

/** Ce vede omul cand serverul refuza clicul (nu cand linkul a expirat). */
function refusalText(e: unknown, lang: Lang): string {
  const t = TEXT[lang]
  if (e instanceof DeskLinkError && e.code === 'state_changed') return t.stateChanged
  if (e instanceof DeskLinkError && e.code === 'self_approval') return t.selfApproval
  if (e instanceof DeskLinkError && e.code === 'cab_pending') return t.cabPending
  if (e instanceof DeskLinkError && e.code === 'cab_rejected') return t.cabRejected
  if (e instanceof DeskLinkError && e.code === 'consent_declined') return t.consentDeclined
  if (e instanceof DeskLinkError && e.code === 'approver_login_required') return t.signIn
  if (e instanceof DeskLinkError && e.code === 'not_the_approver') return t.notTheApprover
  if (e instanceof DeskLinkError && e.code === 'code_required') return t.codeRequired
  if (e instanceof DeskLinkError && e.code === 'code_wrong') return t.codeWrong
  if (e instanceof DeskLinkError && e.code === 'code_locked') return t.codeLocked
  if (e instanceof DeskLinkError && e.code === 'code_expired') return t.codeExpired
  if (e instanceof DeskLinkError && e.code === 'daily_code_limit') return t.codeLimit
  return t.error
}

function LinkGone({ lang }: { lang?: string }) {
  const t = TEXT[pageLang(lang)]
  return (
    <Frame>
      <h1 style={{ margin: '0 0 .75em', fontSize: '1.35em' }}>{t.goneTitle}</h1>
      <p style={{ margin: 0 }}>{t.goneBody}</p>
    </Frame>
  )
}

function LoadFailed() {
  return (
    <Frame>
      <p>{TEXT[pageLang()].error}</p>
    </Frame>
  )
}

function Refused({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <div className="alert alert-warning" role="alert" style={{ margin: '0 0 12px' }}>
      {message}
    </div>
  )
}

function Thanks({ message }: { message: string }) {
  return (
    <Frame>
      <div className="alert alert-success text-center" style={{ margin: 0 }}>
        {message}
      </div>
    </Frame>
  )
}

export function DeskAnswerPage({ token, answer }: { token: string; answer: string }) {
  useNoReferrer()
  const q = useQuery({
    queryKey: ['desk', 'answer', token, answer],
    queryFn: () => fetchAnswer(token, answer),
    retry: false,
  })
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [gone, setGone] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)

  if (gone) return <LinkGone lang={q.data?.lang} />
  if (done) return <Thanks message={done} />

  if (q.isLoading) {
    return (
      <Frame>
        <Spinner />
      </Frame>
    )
  }

  if (q.isError) {
    if (q.error instanceof TicketLinkGone) return <LinkGone />
    return <LoadFailed />
  }

  const data = q.data!
  const label = data.labels.confirm ?? 'Confirm'

  async function confirm() {
    if (saving) return
    setSaving(true)
    try {
      const res = await sendAnswer(token, answer)
      setDone(res.message)
    } catch (e) {
      if (e instanceof TicketLinkGone) setGone(true)
      else setRefusal(refusalText(e, pageLang(data.lang)))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Frame>
      <Refused message={refusal} />
      <p style={{ marginTop: 0 }}>{data.question}</p>
      <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void confirm()}>
        {label}
      </button>
    </Frame>
  )
}

function DeskDecisionPage({
  token,
  kind,
  fetchPage,
  submit,
  primaryDecision,
  secondaryDecision,
  primaryLabelKey,
  secondaryLabelKey,
}: {
  token: string
  kind: 'confirm' | 'approve'
  fetchPage: (t: string) => Promise<DeskDecisionPageData>
  submit: (t: string, d: string, code: string) => Promise<{ message: string }>
  primaryDecision: string
  secondaryDecision: string
  primaryLabelKey: string
  secondaryLabelKey: string
}) {
  useNoReferrer()
  const q = useQuery({
    queryKey: ['desk', kind, token],
    queryFn: () => fetchPage(token),
    retry: false,
  })
  const [saving, setSaving] = useState<'primary' | 'secondary' | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [gone, setGone] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [resent, setResent] = useState(false)

  if (gone) return <LinkGone lang={q.data?.lang} />
  if (done) return <Thanks message={done} />

  if (q.isLoading) {
    return (
      <Frame>
        <Spinner />
      </Frame>
    )
  }

  if (q.isError) {
    if (q.error instanceof TicketLinkGone) return <LinkGone />
    return <LoadFailed />
  }

  const data = q.data!
  const proof = data.proof
  const lang = pageLang(data.lang)
  const codeReady = code.replace(/[\s-]+/g, '').length === 8
  /* Fara dovada nu decide nimeni: nici aprobare, nici refuz. */
  const unproven =
    proof !== undefined &&
    (proof.blocked !== null || (proof.kind === 'session' ? !proof.is_approver : !codeReady))
  const cabPending = data.cab?.state === 'pending'
  const consentDeclined = data.consent?.state === 'declined'
  const primaryLabel = data.labels[primaryLabelKey] ?? (kind === 'confirm' ? 'Yes' : 'Approve')
  const secondaryLabel = data.labels[secondaryLabelKey] ?? (kind === 'confirm' ? 'No' : 'Deny')

  async function act(decision: string, which: 'primary' | 'secondary') {
    if (saving) return
    setSaving(which)
    try {
      const res = await submit(token, decision, proof?.kind === 'code' ? code.trim() : '')
      setDone(res.message)
    } catch (e) {
      if (e instanceof TicketLinkGone) setGone(true)
      else setRefusal(refusalText(e, pageLang(data.lang)))
    } finally {
      setSaving(null)
    }
  }

  async function resend() {
    setResent(false)
    try {
      await resendDeskApproveCode(token)
      setResent(true)
      setRefusal(null)
    } catch (e) {
      if (e instanceof TicketLinkGone) setGone(true)
      else setRefusal(refusalText(e, lang))
    }
  }

  return (
    <Frame>
      <Refused message={refusal} />
      <p style={{ marginTop: 0 }}>{data.question}</p>
      {data.cab && <ChangeFacts page={data} />}
      {proof && (
        <ApproverProof
          proof={proof}
          lang={lang}
          code={code}
          onCode={setCode}
          onResend={() => void resend()}
          resent={resent}
        />
      )}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving !== null || cabPending || consentDeclined || unproven}
          title={
            consentDeclined
              ? 'Approve is disabled: the requester declined the window'
              : cabPending
                ? 'Approve is disabled while the CAB is pending'
                : undefined
          }
          onClick={() => void act(primaryDecision, 'primary')}
        >
          {primaryLabel}
        </button>
        <button
          type="button"
          className="btn btn-default"
          disabled={saving !== null || unproven}
          onClick={() => void act(secondaryDecision, 'secondary')}
        >
          {secondaryLabel}
        </button>
      </div>
    </Frame>
  )
}

function ApproverProof({
  proof,
  lang,
  code,
  onCode,
  onResend,
  resent,
}: {
  proof: DeskApproverProof
  lang: Lang
  code: string
  onCode: (v: string) => void
  onResend: () => void
  resent: boolean
}) {
  const t = TEXT[lang]
  if (proof.blocked === 'self_approval') {
    return <p className="od-approver-proof">{t.selfApproval}</p>
  }
  if (proof.kind === 'session') {
    if (proof.is_approver) return null
    return (
      <p className="od-approver-proof">
        {proof.signed_in ? t.notTheApprover : t.signIn}{' '}
        {!proof.signed_in && (
          <a href="/login" target="_blank" rel="noreferrer noopener">
            {t.signInLink}
          </a>
        )}
      </p>
    )
  }
  return (
    <div className="od-approver-proof" style={{ margin: '0 0 12px' }}>
      <p style={{ marginTop: 0 }}>{proof.code_sent ? t.codeHint.replace('{to}', proof.sent_to) : t.codeNotSent}</p>
      <label style={{ display: 'block', fontSize: 14 }}>
        {t.codeLabel}
        <input
          className="form-control"
          value={code}
          onChange={(e) => onCode(e.target.value)}
          autoComplete="one-time-code"
          maxLength={9}
          style={{ marginTop: 6, maxWidth: '12em', letterSpacing: '.1em' }}
        />
      </label>
      <button type="button" className="btn btn-link" style={{ paddingLeft: 0 }} onClick={onResend}>
        {t.codeResend}
      </button>
      {resent && <span style={{ fontSize: 14 }}> {t.codeResent}</span>}
    </div>
  )
}

type DeskDecisionPageData = {
  ticket: number
  question: string
  labels: Record<string, string>
  lang?: string
  cab?: { state: string; ticket_id: number }
  consent?: { state: string }
  window?: { start: string; end: string }
  proof?: DeskApproverProof
}

const CAB_TEXT: Record<string, string> = {
  none: 'no CAB step',
  standard: 'standard change (pre-approved)',
  pending: 'pending',
  approved: 'approved',
  rejected: 'rejected',
}

const CONSENT_TEXT: Record<string, string> = {
  given: 'requester agreed',
  declined: 'requester declined',
  pending: 'requester has not answered',
}

/* Pagina aprobatorului e in engleza (capitolul 6, F8): CAB-ul, fereastra, consimtamantul. */
function ChangeFacts({ page }: { page: DeskDecisionPageData }) {
  const cab = page.cab!
  return (
    <ul className="desk-change-facts" style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 14 }}>
      <li>
        CAB: {CAB_TEXT[cab.state] ?? cab.state}
        {cab.ticket_id > 0 ? ` (change ticket #${cab.ticket_id})` : ''}
      </li>
      {page.window && page.window.start !== '' && (
        <li>
          Window: {page.window.start} – {page.window.end}
        </li>
      )}
      {page.consent && CONSENT_TEXT[page.consent.state] && <li>{CONSENT_TEXT[page.consent.state]}</li>}
      {cab.state === 'pending' && <li>Approve is disabled while the CAB is pending.</li>}
    </ul>
  )
}

export function DeskConfirmPage({ token }: { token: string }) {
  return (
    <DeskDecisionPage
      token={token}
      kind="confirm"
      fetchPage={fetchDeskConfirm}
      submit={(t, d) => submitDeskConfirm(t, d as 'yes' | 'no')}
      primaryDecision="yes"
      secondaryDecision="no"
      primaryLabelKey="yes"
      secondaryLabelKey="no"
    />
  )
}

export function DeskApprovePage({ token }: { token: string }) {
  return (
    <DeskDecisionPage
      token={token}
      kind="approve"
      fetchPage={fetchDeskApprove}
      submit={(t, d, code) => submitDeskApprove(t, d as 'approve' | 'deny', code)}
      primaryDecision="approve"
      secondaryDecision="deny"
      primaryLabelKey="approve"
      secondaryLabelKey="deny"
    />
  )
}

function consentSentence(lang: string, draftId: number, machines: number, windowText: string): string {
  const n = Math.max(0, Math.floor(machines || 0))
  if (lang === 'ro') {
    // Româna cere „de” de la 20 în sus, afară de 101–119, 201–219...
    const count = n === 1 ? '1 mașină' : n >= 20 && (n % 100 === 0 || n % 100 >= 20) ? `${n} de mașini` : `${n} mașini`
    return n > 0
      ? `Propunerea de schimbare #${draftId} ar rula pe ${count} în fereastra: ${windowText}.`
      : `Propunerea de schimbare #${draftId} ar rula în fereastra: ${windowText}.`
  }
  const count = n === 1 ? '1 machine' : `${n} machines`
  return n > 0
    ? `Change proposal #${draftId} would run on ${count} during: ${windowText}.`
    : `Change proposal #${draftId} would run during: ${windowText}.`
}

export function DeskConsentPage({ token }: { token: string }) {
  useNoReferrer()
  const q = useQuery({
    queryKey: ['desk', 'consent', token],
    queryFn: () => fetchDeskConsent(token),
    retry: false,
  })
  const [saving, setSaving] = useState<'agree' | 'decline' | null>(null)
  const [note, setNote] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const [gone, setGone] = useState(false)
  const [refusal, setRefusal] = useState<string | null>(null)

  if (gone) return <LinkGone lang={q.data?.lang} />
  if (done) return <Thanks message={done} />

  if (q.isLoading) {
    return (
      <Frame>
        <Spinner />
      </Frame>
    )
  }

  if (q.isError) {
    if (q.error instanceof TicketLinkGone) return <LinkGone />
    return <LoadFailed />
  }

  const data = q.data!
  const labels = data.labels
  const windowText =
    data.change.window_start !== '' && data.change.window_end !== ''
      ? `${data.change.window_start} – ${data.change.window_end}`
      : labels.consent_window ?? 'the proposed window'

  async function act(answer: 'agree' | 'decline') {
    if (saving) return
    setSaving(answer)
    try {
      const res = await submitDeskConsent(token, answer, answer === 'decline' ? note : '')
      setDone(res.message)
    } catch (e) {
      if (e instanceof TicketLinkGone) setGone(true)
      else setRefusal(refusalText(e, pageLang(data.lang)))
    } finally {
      setSaving(null)
    }
  }

  return (
    <Frame>
      <Refused message={refusal} />
      <h1 style={{ margin: '0 0 .75em', fontSize: '1.25em' }}>{data.change.title}</h1>
      <p style={{ marginTop: 0 }}>{consentSentence(data.lang, data.change.draft_id, data.change.machines, windowText)}</p>
      <label style={{ display: 'block', marginBottom: 12, fontSize: 14 }}>
        {labels.consent_note ?? 'Suggestion (optional)'}
        <textarea
          className="form-control"
          rows={3}
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          style={{ marginTop: 6 }}
        />
      </label>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" disabled={saving !== null} onClick={() => void act('agree')}>
          {labels.consent_agree ?? 'The window works for me'}
        </button>
        <button type="button" className="btn btn-default" disabled={saving !== null} onClick={() => void act('decline')}>
          {labels.consent_decline ?? 'The window does not work'}
        </button>
      </div>
    </Frame>
  )
}

/** Pagina tichetului deschisă cu jetonul `page` al biroului. */
export function DeskTicketPage({ token }: { token: string }) {
  useNoReferrer()
  return <CustomerTicketPage token={token} desk />
}
